import React, { useContext, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import parse, { domToReact, HTMLReactParserOptions } from 'html-react-parser';
import { Link, Redirect, RouteComponentProps } from 'react-router-dom';
import { StaticContext } from 'react-router';
import { BiPrinter } from 'react-icons/bi';

import { usePrefetchSuspense } from 'src/lib/usePrefetch';
import { useFetch } from 'src/lib/FetchContext';

import { Showcase, SocialSharingContext, SocialSharingProvider } from '@riac/design-system';
import { Spinner } from '@riac/design-system';

import { Page404 } from 'src/pages/Page404';
import { OutlineButton, TextButton } from '@riac/design-system';
import { YoutubeLazyLoader } from '@riac/design-system';
import { LazyImg } from '@riac/design-system';
import { ImgLazyLoader } from '@riac/design-system';
import { base64ToString, stringToBase64 } from 'src/lib/IsometricBase64';


import { AccountContextProvider } from 'src/lib/AccountContext';
import { Content, GhostPostSingleComments, GhostPostSingleContainer,
  GhostPostSinglePanels, PictureContainer, UsePrintButtonMessage } from './GhostPostSingle.style';


export type GhostPostSingleProps = React.PropsWithChildren<RouteComponentProps<any, StaticContext, unknown>>;


const GhostPostSingleScriptWrapper = ({ attribs }: React.PropsWithChildren<{attribs: string[]}>): JSX.Element => {
  const scriptContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = document.createElement('script');
    Object.assign(el, attribs);
    scriptContainerRef.current.appendChild(el);
  }, []);
  return <div ref={scriptContainerRef}></div>;
};

const GhostPostSingle = ({ match } : GhostPostSingleProps): JSX.Element => {
  const isBlog = match.path.includes('riac-blog');
  const { fetch } = useFetch();
  const tags = isBlog ? 'blogs' : 'news';
  if(match.params.title.includes('~')) {
    return <Redirect to={`/${isBlog ? 'riac-blog' : 'news'}/${match.params.title.replace(/~/g, '')}`}/>;
  }
  const { printButtonUsed } = useContext(SocialSharingContext);

  const data = usePrefetchSuspense(`ghost-post-single-${match.params.title}-${tags}`, async () => {
    // determine tags for fetch
    // Get individual blog post
    const req = await fetch(`/api/ghost/getGhostSingleData/${match.params.title}/${tags}/3`);

    const {
      success,
      blogPost,
      recentPosts,
      comments,
    } = await req.json();

    const recent = [];

    if(success === true)
    {
      await recentPosts.map((post) => {
        const parts = post.thumbnail.split('.');
        return recent.push({
          image: /(png|jpe?g)/i.test(parts[parts.length - 1]) ? post.thumbnail : post.image,
          title: post.title,
          content: post.excerpt,
          navigation: { url: `/${isBlog ? 'riac-blog' : 'news'}/${post.slug}`, text: 'Read more', variant: 'link' },
        });
      });

      blogPost.html = stringToBase64(blogPost.html);
    }

    return { success, post: blogPost, recent, comments };
  }).read();

  let isLoading = false;
  if(data && match.params.title.replace(/~/g, '') === data?.post?.slug) {
    isLoading = false;
  } else {
    isLoading = true;
  }

  // Convert html to React JSX and
  // Parse Ghost post url's and add internal routing Link's
  const parseWithLinks = (html: string): string | JSX.Element | JSX.Element[] => {
    // These are incorrectly picked up by eslint as unmodified.
    // This is because they are set only once, after options is declared
    // However, options needs a reference to these variables before they are set.
    // Frankly this is a silly way of doing this but fuck it I'm not
    // reworking it.

    // eslint-disable-next-line prefer-const
    let mainOptions: HTMLReactParserOptions;
    // eslint-disable-next-line prefer-const
    let noImgOptions: HTMLReactParserOptions;

    const options: (processimg?: boolean) => HTMLReactParserOptions = (processimg = true) => ({
      replace: (node) => {
        const { name, attribs, children, parent } = node as any;
        if(name === 'a' && attribs.href) {
          let link: URL | string = '';
          try {
            link = new URL(attribs.href);
            if(link.hostname === 'www.rockislandauction.com') {
              link = link.pathname;
              return <Link to={link}>{domToReact(children, mainOptions)}</Link>;
            }
            else if(parent.attribs.class === 'kg-callout-text') {
              return <OutlineButton to={attribs.href}>{domToReact(children, mainOptions)}</OutlineButton>;
            }
            else
            {
              return <TextButton to={attribs.href}>{domToReact(children, mainOptions)}</TextButton>;
            }
          } catch (e) {
            return;
          }
        }
        if(name === 'html') {
          return <>{domToReact(children, mainOptions)}</>;
        }
        if(name === 'head') {
          return <>{domToReact(children, mainOptions)}</>;
        }
        if(name === 'body') {
          return <>{domToReact(children, mainOptions)}</>;
        }
        if(name == 'picture') {
          return <PictureContainer>
            <picture>
              <ImgLazyLoader>{domToReact(children, noImgOptions)}</ImgLazyLoader>
            </picture>
          </PictureContainer>;
        }
        if(name == 'script') {
          return <GhostPostSingleScriptWrapper attribs={attribs}/>;
        }
        if(processimg) {
          if(name == 'img')
          {
            if(attribs.class)
            {
              if(attribs.class)
              {
                attribs.className = attribs.class;
                delete attribs.class;
              }
              return <LazyImg {... attribs}/>;
            }
          }
          if( name == 'iframe' && attribs.src.indexOf('youtube') !== (-1))
          {
            return <YoutubeLazyLoader { ...attribs }/>;
          }
        }
        else
        {
          if(name == 'img')
          {
            if(attribs.loading)
            {
              delete attribs.loading;
              return node;
            }
          }
        }
      },
    });
    mainOptions = options();
    noImgOptions = options(false);

    return parse(html, mainOptions);
  };
  if(data?.post?.success === 'false' || data?.success === false) {
    return <Page404/>;
  }

  return (
    <AccountContextProvider>
      <GhostPostSingleContainer printButtonUsed = {printButtonUsed}>
        <UsePrintButtonMessage>
            Please use the print button in the share bar at the top of the page.
          <div><BiPrinter/></div>
        </UsePrintButtonMessage>
        <Spinner loading={isLoading} size='lg'>
          <Showcase
            title={data?.post?.title}
            subtitle={dayjs(data?.post?.published_at).format('MMMM D, YYYY')}
            content={data?.post?.author_name?.length > 0 ? `By ${data?.post?.author_name[0]}` : ''}
            image={data?.post?.image}
            align='left'
            small
            sharing
          />
          {data?.post?.html
                  && <Content>
                    {parseWithLinks(base64ToString(data?.post?.html))}
                  </Content>
          }
          <GhostPostSinglePanels
            title={'Recent ' + (isBlog ? 'Posts' : 'News') }
            type='card'
            items={data?.recent}
          />
          {(data && isBlog) && <GhostPostSingleComments post={data?.post} comments={data?.comments} />}
        </Spinner>
      </GhostPostSingleContainer>
      {(!isLoading && data?.post?.success) && <Page404/>}
    </AccountContextProvider>
  );
};

export default (props: GhostPostSingleProps): JSX.Element => {
  return (
    <SocialSharingProvider>
      <GhostPostSingle {...props}/>
    </SocialSharingProvider>
  );
};
